import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  createSMSTemplateItemService,
  deleteSMSTemplateItemService,
  editSMSTemplateItemService,
  getSMSTemplateItemsService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const smsTemplateAdminContext = createContext({
  smsTemplateItems: [],
  setSMSTemplateItems: () => {},
  smsTemplate: {},
  setSMSTemplate: () => {},
  id: "",
  setId: () => {},
  title: "",
  setTitle: () => {},
  message: "",
  setMessage: () => {},
  description: "",
  setDescription: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  isShowDetailsModal: null,
  setIsShowDetailsModal: () => {},
  totalCount: null,
  setTotalCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  limit: null,
  setLimit: () => {},
  handleShowModal: () => {},
  handleCloseModal: () => {},
  handleShowDetailsModal: () => {},
  handleCloseDetailsModal: () => {},
  handleGetSMSTemplateItemList: () => {},
  handleCreateSMSTemplateItem: () => {},
  handleEditSMSTemplateItem: () => {},
  handleDeleteSMSTemplateItem: () => {},
});

const SMSTemplateAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [smsTemplateItems, setSMSTemplateItems] = useState([]);
  const [smsTemplate, setSMSTemplate] = useState({});
  const [id, setId] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowDetailsModal, setIsShowDetailsModal] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(100);

  const handleResetState = () => {
    setId("");
    setTitle("");
    setMessage("");
    setDescription("");
  };

  // Create User Satisfaction Modal Operations
  const handleShowModal = (itemValue) => {
    setIsShowModal(true);
    if (itemValue) {
      setId(itemValue.id);
      setTitle(itemValue.title);
      setMessage(itemValue.message);
      setDescription(itemValue.description);
    }
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  // Details User Satisfaction Modal Operations
  const handleShowDetailsModal = (value) => {
    setIsShowDetailsModal(true);
    setSMSTemplate(value);
  };
  const handleCloseDetailsModal = () => {
    setIsShowDetailsModal(false);
    setSMSTemplate({});
  };

  // Handle Get User Satisfaction Item List Method
  const handleGetSMSTemplateItemList = async (event) => {
    event?.preventDefault();
    const isSearch = !isEmpty(event);

    const objData = {
      offset: ((isSearch ? 1 : pageNumber) - 1) * limit,
      limit,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getSMSTemplateItemsService(objData);
      if (status === 200) {
        setSMSTemplateItems(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create User Satisfaction Item Method
  const handleCreateSMSTemplateItem = async (event) => {
    event?.preventDefault();

    const objData = {
      title,
      message,
      description,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createSMSTemplateItemService(objData);
      if (status === 201) {
        toastSuccessMessage("آیتم با موفقیت ایجاد شد");
        handleGetSMSTemplateItemList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit User Satisfaction Item Method
  const handleEditSMSTemplateItem = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      title,
      message,
      description,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editSMSTemplateItemService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetSMSTemplateItemList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete User Satisfaction Item Method
  const handleDeleteSMSTemplateItem = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteSMSTemplateItemService(id);
      if (status === 200) {
        toastSuccessMessage(`آیتم  ${id} با موفقیت حذف شد`);
        handleGetSMSTemplateItemList();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <smsTemplateAdminContext.Provider
      value={{
        smsTemplateItems,
        setSMSTemplateItems,
        smsTemplate,
        setSMSTemplate,
        totalCount,
        setTotalCount,
        pageNumber,
        setPageNumber,
        limit,
        setLimit,
        id,
        setId,
        title,
        setTitle,
        message,
        setMessage,
        description,
        setDescription,
        isShowModal,
        setIsShowModal,
        isShowDetailsModal,
        setIsShowDetailsModal,
        handleShowDetailsModal,
        handleCloseDetailsModal,
        handleShowModal,
        handleCloseModal,
        handleGetSMSTemplateItemList,
        handleCreateSMSTemplateItem,
        handleEditSMSTemplateItem,
        handleDeleteSMSTemplateItem,
      }}
    >
      {children}
    </smsTemplateAdminContext.Provider>
  );
};

export default SMSTemplateAdminContext;
